import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearch } from '../../hooks/useSearch';
import { scrollToAnchor, scrollToAnchorWithRetry } from '../../utils/scrollUtils';
import './search.css';

/* ─── SearchBar ───────────────────────────────────────────────────────────── */
const SearchBar = () => {
  const { query, results, isSearching, search, clear } = useSearch();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const pendingAnchorRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const closeModal = useCallback(() => {
    setModalOpen(false);
    clear();
    setActiveIndex(-1);
  }, [clear]);

  // After generic cross-page navigation, fire scroll retry
  useEffect(() => {
    if (!pendingAnchorRef.current) return;
    const anchor = pendingAnchorRef.current;
    pendingAnchorRef.current = null;
    scrollToAnchorWithRetry(anchor);
  }, [location.pathname]);

  const handleSelect = useCallback((result) => {
    closeModal();
    if (!result.path) return;

    const isSamePage = result.path === location.pathname;
    const pageHandlesScroll = result.tab != null || result.path === '/help';

    if (isSamePage && !pageHandlesScroll) {
      if (result.anchor) setTimeout(() => scrollToAnchor(result.anchor), 80);
      return;
    }
    if (pageHandlesScroll) {
      navigate(result.path, {
        state: { searchTarget: { tab: result.tab ?? null, anchor: result.anchor ?? null } },
      });
    } else {
      if (result.anchor) pendingAnchorRef.current = result.anchor;
      navigate(result.path);
    }
  }, [navigate, closeModal, location.pathname]);

  return (
    <>
      {/* Fixed-size slot in the navbar flex row — never changes size */}
      <div className="search-wrap">
        <button
          className="search-icon-btn"
          onClick={() => setModalOpen(true)}
          aria-label="Open search"
          aria-haspopup="dialog"
        >
          <SearchIcon />
        </button>
      </div>

      {/* Modal is position:fixed — completely outside navbar layout */}
      {modalOpen && (
        <SearchModal
          query={query}
          results={results}
          isSearching={isSearching}
          activeIndex={activeIndex}
          onSearch={(val) => { search(val); setActiveIndex(-1); }}
          setActiveIndex={setActiveIndex}
          onClose={closeModal}
          onSelect={handleSelect}
        />
      )}
    </>
  );
};

/* ─── SearchModal ─────────────────────────────────────────────────────────── */
const SearchModal = ({
  query, results, isSearching, activeIndex,
  onSearch, setActiveIndex, onClose, onSelect,
}) => {
  const inputRef = useRef(null);
  const showResults = query.length > 1;

  // Auto-focus and body-scroll lock on open
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc closes
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Click on backdrop (not dialog) closes
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      onSelect(results[activeIndex]);
    }
  };

  return (
    <div
      className="search-modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="search-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Search museum"
      >
        {/* Input row */}
        <div className="search-modal-header">
          <span className="search-modal-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            className="search-modal-input"
            type="text"
            placeholder="Search exhibits, shows, visitor info…"
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search"
            autoComplete="off"
          />
          <button
            className="search-modal-close-btn"
            onClick={onClose}
            aria-label="Close search"
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Results */}
        {showResults && (
          <div className="search-modal-results" role="listbox" aria-label="Search results">
            {isSearching && (
              <div className="search-modal-status">Searching…</div>
            )}
            {!isSearching && results.length === 0 && (
              <div className="search-modal-status">No results for "{query}"</div>
            )}
            {results.map((result, i) => (
              <button
                key={i}
                type="button"
                className={`search-modal-result-item${activeIndex === i ? ' search-modal-result-item--active' : ''}`}
                role="option"
                aria-selected={activeIndex === i}
                onClick={() => onSelect(result)}
              >
                <span className="search-modal-result-title">{result.title}</span>
                <span className="search-modal-result-meta">{result.page}</span>
                {result.excerpt && (
                  <span className="search-modal-result-excerpt">
                    {result.excerpt.length > 110
                      ? result.excerpt.slice(0, 110) + '…'
                      : result.excerpt}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Icons ───────────────────────────────────────────────────────────────── */
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8"/>
    <path d="M21 21l-4.35-4.35"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);

export default SearchBar;
